import React, { useEffect, useState } from 'react';

export default function OutsiderTrading() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="bg-black text-white">
      {/* Hero Section */}
      <HeroSection scrollY={scrollY} />

      {/* Text Reveal Sections */}
      <TextRevealSection
        scrollY={scrollY}
        triggerPoint={800}
        line1="Insider Trading"
        line2="from the Outside"
      />

      <TextRevealSection
        scrollY={scrollY}
        triggerPoint={2200}
        line1="All of the Reward"
        line2="None of the Risk"
        gradient="from-green-400 to-emerald-500"
      />

      <TextRevealSection
        scrollY={scrollY}
        triggerPoint={3600}
        line1="We leverage cutting-edge AI"
        line2="to analyze publicly available data"
        gradient="from-blue-400 to-cyan-500"
        smaller
      />

      <TextRevealSection
        scrollY={scrollY}
        triggerPoint={5000}
        line1="No insider information"
        line2="Just superior technology"
        reducedSpacing
      />

      {/* Feature Grid */}
      <div className="min-h-screen flex items-center justify-center px-4 py-20">
        <div className="max-w-6xl w-full">
          <h2 className="text-5xl md:text-6xl font-bold mb-16 text-center">
            Why Outsider Trading?
          </h2>
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            <FeatureCard
              title="100% Legal"
              description="Stay on the right side of the SEC while maximizing returns"
              icon="⚖️"
            />
            <FeatureCard
              title="AI-Powered"
              description="Machine learning algorithms that never sleep"
              icon="🤖"
            />
            <FeatureCard
              title="Real-time"
              description="Millisecond-level market analysis and execution"
              icon="⚡"
            />
          </div>
          <div className="grid md:grid-cols-3 gap-12 text-center">
            <StatCard number="847%" label="Average Annual Returns*" />
            <StatCard number="0" label="Insider Trading Charges" />
            <StatCard number="24/7" label="Market Monitoring" />
          </div>
          <p className="text-gray-600 text-sm text-center mt-12">
            *Past performance does not guarantee future results. Probably.
          </p>
        </div>
      </div>

      {/* Final CTA */}
      <div className="h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-4xl">
          <h2 className="text-6xl md:text-8xl font-bold mb-8">
            Ready to trade like an{' '}
            <span className="italic text-gray-500">outsider</span>?
          </h2>
          <button className="px-12 py-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full text-2xl font-bold hover:scale-105 transition-transform">
            Get Started
          </button>
        </div>
      </div>
    </div>
  );
}

function HeroSection({ scrollY }) {
  const opacity = Math.max(0, 1 - scrollY / 500);
  const scale = Math.max(0.8, 1 - scrollY / 2000);

  return (
    <div
      className="h-screen flex items-center justify-center"
      style={{
        opacity,
        transform: `scale(${scale})`,
      }}
    >
      <div className="text-center px-4">
        <h1 className="text-7xl md:text-9xl font-bold mb-8 tracking-tight">
          OUTSIDER
          <br />
          <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 text-transparent bg-clip-text">
            TRADING
          </span>
        </h1>
        <p className="text-xl md:text-2xl text-gray-400">
          The future of financial intelligence
        </p>
      </div>
    </div>
  );
}

function TextRevealSection({ scrollY, triggerPoint, line1, line2, gradient, smaller, reducedSpacing }) {
  const textSize = smaller ? "text-4xl md:text-6xl" : "text-5xl md:text-7xl";

  // Line 1 appears at triggerPoint
  const line1Start = triggerPoint;
  const line1Opacity = Math.min(1, Math.max(0, (scrollY - line1Start) / 300));

  // Line 2 appears 400px later
  const line2Start = triggerPoint + 400;
  const line2Opacity = Math.min(1, Math.max(0, (scrollY - line2Start) / 300));

  // Both fade out 800px after line1Start
  const fadeOutStart = triggerPoint + 800;
  const fadeOutProgress = Math.min(1, Math.max(0, (scrollY - fadeOutStart) / 300));
  const sectionOpacity = 1 - fadeOutProgress;

  const bottomSpacing = reducedSpacing ? "h-96" : "h-screen";

  return (
    <>
      <div className="h-screen" />
      <div className="fixed top-0 left-0 right-0 h-screen flex items-center justify-center px-4 pointer-events-none">
        <div className="max-w-4xl" style={{ opacity: sectionOpacity }}>
          <h2 className={`${textSize} font-bold mb-6`}>
            <span
              className={gradient ? `bg-gradient-to-r ${gradient} text-transparent bg-clip-text` : ''}
              style={{ opacity: line1Opacity }}
            >
              {line1}
            </span>
            <br />
            <span className="text-gray-500" style={{ opacity: line2Opacity }}>
              {line2}
            </span>
          </h2>
        </div>
      </div>
      <div className={bottomSpacing} />
    </>
  );
}

function FeatureCard({ title, description, icon }) {
  return (
    <div className="border border-gray-800 rounded-2xl p-8 hover:border-gray-600 transition-colors bg-gradient-to-br from-gray-900 to-black">
      <div className="text-5xl mb-4">{icon}</div>
      <h3 className="text-2xl font-bold mb-3">{title}</h3>
      <p className="text-gray-400">{description}</p>
    </div>
  );
}

function StatCard({ number, label }) {
  return (
    <div>
      <div className="text-6xl md:text-7xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text mb-4">
        {number}
      </div>
      <div className="text-xl text-gray-400">{label}</div>
    </div>
  );
}